import { useCallback, useEffect, useMemo, useState } from "react";
import { listCompanies } from "@/api/companies";
import type { Company, SavedViewRead } from "@/api/types";
import { useDebounce } from "@/hooks/useDebounce";
import { pickNumber, pickRaw, pickString } from "@/lib/viewFilters";

export type DMFilter = "all" | "yes" | "no";

/**
 * The Contacts page's filters — search, company, decision maker,
 * enrichment — as its saved views store them and its list request takes
 * them. Shared by the Contacts page and every list that offers the same
 * filters (the Email wizard).
 */
export function useContactFilterState(slug: string | null, enabled = true) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [companyFilter, setCompanyFilter] = useState<number | null>(null);
  const [dmFilter, setDmFilter] = useState<DMFilter>("all");
  const [enrichmentFilter, setEnrichmentFilter] = useState<string | null>(null);

  // Initial display-name cache; company selection searches the server separately.
  const [companies, setCompanies] = useState<Company[]>([]);
  useEffect(() => {
    if (!slug || !enabled) return;
    let cancelled = false;
    listCompanies(slug, { page_size: 200 })
      .then((res) => !cancelled && setCompanies(res.items))
      .catch(() => !cancelled && setCompanies([]));
    return () => {
      cancelled = true;
    };
  }, [slug, enabled]);

  const companyById = useMemo(() => {
    const m = new Map<number, Company>();
    for (const c of companies) m.set(c.id, c);
    return m;
  }, [companies]);

  /** Filter on a company picked in the search select, keeping its name. */
  const pickCompany = useCallback((company: Company | null) => {
    setCompanyFilter(company?.id ?? null);
    if (company) {
      setCompanies((previous) => [...previous.filter((item) => item.id !== company.id), company]);
    }
  }, []);

  /** What the list request takes. The search is the consumer's: the page
   *  sends it as `q`, the wizard as its own `search`; both ask the server. */
  const listParams = useMemo(
    () => ({
      company_id: companyFilter ?? undefined,
      is_decision_maker: dmFilter === "all" ? undefined : dmFilter === "yes",
      enrichment_status: enrichmentFilter ?? undefined,
    }),
    [companyFilter, dmFilter, enrichmentFilter],
  );

  // Filter keys are snake_case to match the backend API params (and the seed
  // data). Saved views may be in either snake_case (canonical) or camelCase
  // (legacy) — `applyView` reads both.
  const filtersJson = useMemo<Record<string, unknown>>(
    () => ({
      search: search.trim(),
      company_id: companyFilter,
      is_decision_maker: dmFilter === "yes" ? true : dmFilter === "no" ? false : null,
      enrichment_status: enrichmentFilter,
    }),
    [search, companyFilter, dmFilter, enrichmentFilter],
  );

  const applyView = useCallback((view: SavedViewRead) => {
    const f = view.filters as Record<string, unknown>;
    setSearch(pickString(f, "search") ?? "");
    setCompanyFilter(pickNumber(f, "company_id", "companyId"));
    const dm = pickRaw(f, "is_decision_maker", "dmFilter");
    if (dm === true || dm === "yes") setDmFilter("yes");
    else if (dm === false || dm === "no") setDmFilter("no");
    else setDmFilter("all");
    setEnrichmentFilter(pickString(f, "enrichment_status", "enrichmentFilter"));
  }, []);

  const anyFilterActive =
    debouncedSearch.trim() !== "" || companyFilter !== null || dmFilter !== "all" || enrichmentFilter !== null;

  return {
    search,
    setSearch,
    debouncedSearch,
    companyFilter,
    dmFilter,
    setDmFilter,
    enrichmentFilter,
    setEnrichmentFilter,
    /** The companies whose names are known (for the select and the create dialog). */
    companies,
    companyById,
    pickCompany,
    listParams,
    filtersJson,
    applyView,
    anyFilterActive,
  };
}

export type ContactFilterState = ReturnType<typeof useContactFilterState>;
